import { OmakaseDesktopHeaderSearchTrigger } from 'components/header/OmakaseDesktopHeaderSearchTrigger';
import { Popover } from 'ui';
import { SearchBarOption, SearchBarOptionOrSectionDivider } from 'utils/searchBar';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { getHandleSelectEntryReservations } from './SearchNav';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useCurrentMetroArea } from 'hooks/useCurrentMetroArea';
import { useState } from 'react';
import SearchBarHeaderDesktop from 'components/nav-search/SearchBarHeaderDesktop';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      alignItems: 'center',
      backgroundColor: theme.colors.bgDefault,
      border: `1px solid ${theme.colors.borderDefault}`,
      borderRadius: 0,
      color: theme.colors.fgSubtle,
      display: 'flex',
      flex: `0 1 ${theme.typography.pxToRem(373)}`,
      maxWidth: theme.typography.pxToRem(420),
      minWidth: theme.typography.pxToRem(200),
      justifyContent: 'flex-start',
      padding: `${theme.typography.pxToRem(11)} ${theme.typography.pxToRem(14)}`,
      paddingLeft: theme.typography.pxToRem(12),
      transition: `color ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut} 0ms,box-shadow ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut} 0ms`,
      '&:hover': {
        borderColor: theme.colors.borderDefault,
        boxShadow: theme.shadow[100],
        color: theme.colors.fgDefault,
        '@media (hover: none)': {
          boxShadow: 'none',
          color: theme.colors.fgSubtle,
        },
      },
      '&.open': {
        borderColor: theme.colors.borderDefault,
        maxWidth: theme.typography.pxToRem(585),
      },
      [theme.breakpoints.up('md')]: {
        minWidth: 0,
      },
    },
    paperRoot: {
      border: `1px solid ${theme.colors.borderDefault}`,
      boxShadow: theme.shadow[100],
      height: theme.typography.pxToRem(648),
      marginTop: theme.typography.pxToRem(-4),
      maxWidth: theme.typography.pxToRem(420),
      overflow: 'hidden',
      width: '100%',
    },
  })
);

export interface SearchNavHeaderDesktopProps {
  autocompleteBusinessSuggestions(searchText: string): Promise<unknown>;
  clearSearchResults(): void;
  currentSearch: string;
  searchResults: SearchBarOption[];
  setCurrentSearch(value: string): void;
}

export const SearchNavHeaderDesktop: React.FunctionComponent<SearchNavHeaderDesktopProps> = ({
  autocompleteBusinessSuggestions,
  clearSearchResults,
  searchResults,
  currentSearch,
  setCurrentSearch,
}) => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  const currentMetroArea = useCurrentMetroArea();
  return (
    <SearchNavDesktopBase
      onClickEntry={getHandleSelectEntryReservations(
        amplitudeSendEvent,
        currentSearch,
        currentMetroArea?.name
      )}
      autocomplete={autocompleteBusinessSuggestions}
      clearSearchResults={clearSearchResults}
      currentSearch={currentSearch}
      searchResults={searchResults}
    />
  );
};

interface SearchNavDesktopBaseProps {
  onClickEntry: (entry: SearchBarOption, idx: number) => void;
  autocomplete(searchText: string): void;
  clearSearchResults(): void;
  currentSearch: string;
  searchResults: SearchBarOptionOrSectionDivider[];
  placeholder?: string;
}

export const SearchNavDesktopBase: React.FunctionComponent<SearchNavDesktopBaseProps> = ({
  autocomplete,
  onClickEntry,
  clearSearchResults,
  searchResults,
  placeholder = 'Find a business, cuisine, or experience',
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const classes = useStyles();

  const handleClickSearch = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    anchorEl === null && setOpen(true);
  };

  const handleClosePopover = () => {
    setTimeout(() => {
      if (anchorEl) {
        anchorEl.blur();
        setAnchorEl(null);
      }
    }, 20);
    setOpen(false);
  };

  const handleSelectEntry = (entry: SearchBarOption, idx: number) => {
    handleClosePopover();
    onClickEntry(entry, idx);
  };

  return (
    <>
      <OmakaseDesktopHeaderSearchTrigger onClick={handleClickSearch} />

      <Popover
        anchorEl={anchorEl}
        open={open}
        onClose={handleClosePopover}
        PaperProps={{
          className: classes.paperRoot,
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <SearchBarHeaderDesktop
          autocomplete={autocomplete}
          clearSearchResults={clearSearchResults}
          onSelectEntry={handleSelectEntry}
          placeholder={placeholder}
          results={searchResults}
        />
      </Popover>
    </>
  );
};
